import { Zap, Cloud, Shield, Users, Handshake, Check } from 'lucide-react';
import { whyChooseUs } from '../data/mock';

const iconMap = {
  Zap: Zap,
  Cloud: Cloud,
  Shield: Shield,
  Users: Users,
  Handshake: Handshake
};

const colorMap = {
  0: { gradient: 'from-[#3B82F6] to-[#60A5FA]', bg: 'bg-[#3B82F6]/10', border: 'border-[#3B82F6]/30', icon: 'text-[#3B82F6]', hover: 'group-hover:bg-[#3B82F6]' },
  1: { gradient: 'from-[#8B5CF6] to-[#A78BFA]', bg: 'bg-[#8B5CF6]/10', border: 'border-[#8B5CF6]/30', icon: 'text-[#8B5CF6]', hover: 'group-hover:bg-[#8B5CF6]' },
  2: { gradient: 'from-[#F97316] to-[#FB923C]', bg: 'bg-[#F97316]/10', border: 'border-[#F97316]/30', icon: 'text-[#F97316]', hover: 'group-hover:bg-[#F97316]' },
  3: { gradient: 'from-[#10B981] to-[#34D399]', bg: 'bg-[#10B981]/10', border: 'border-[#10B981]/30', icon: 'text-[#10B981]', hover: 'group-hover:bg-[#10B981]' },
  4: { gradient: 'from-[#EC4899] to-[#F472B6]', bg: 'bg-[#EC4899]/10', border: 'border-[#EC4899]/30', icon: 'text-[#EC4899]', hover: 'group-hover:bg-[#EC4899]' },
};

const WhyChooseUs = () => {
  return (
    <section className="relative py-24 md:py-32" style={{ background: 'var(--bg-card)' }}>
      {/* Background Accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#3B82F6]/5 rounded-full blur-[200px]"></div>

      <div className="relative max-w-[1400px] mx-auto px-[7.6923%]">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#10B981]/10 border border-[#10B981]/30 rounded-full mb-6">
            <span className="text-[#10B981] text-sm font-semibold">Why Choose Us</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight" style={{ letterSpacing: '-0.02em' }}>
            Why Choose{' '}
            <span className="bg-gradient-to-r from-[#3B82F6] via-[#8B5CF6] to-[#F97316] bg-clip-text text-transparent">
              CLOUD IT
            </span>
          </h2>
          
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            Partner with a team that's committed to your success and equipped with cutting-edge technology.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {whyChooseUs.map((item, index) => {
            const Icon = iconMap[item.icon];
            const colors = colorMap[index % 5];
            return (
              <div 
                key={item.id}
                data-testid={`why-card-${index}`}
                className="group relative"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Card */}
                <div className="bg-[#0A0A0F]/80 backdrop-blur-sm border border-white/10 rounded-2xl p-8 h-full transition-all duration-400 hover:border-white/20 hover:-translate-y-2">
                  {/* Icons */}
                  <div className="flex items-center gap-4 mb-5">
                    <div className={`${colors.bg} w-12 h-12 rounded-xl flex items-center justify-center border ${colors.border} ${colors.hover} transition-all duration-400 group-hover:scale-110`}>
                      <Check size={22} className={`${colors.icon} group-hover:text-white transition-colors duration-400`} />
                    </div>
                    <div className={`${colors.bg} p-3 rounded-xl border ${colors.border}`}>
                      <Icon size={22} className={colors.icon} />
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-white mb-3">
                    {item.title}
                  </h3>
                  
                  <p className="text-white/50 leading-relaxed text-sm">
                    {item.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Statement */}
        <div className="relative bg-gradient-to-r from-[#3B82F6]/10 via-[#8B5CF6]/10 to-[#F97316]/10 border border-white/10 rounded-2xl p-12 text-center overflow-hidden">
          {/* Glow effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#3B82F6]/5 via-transparent to-[#F97316]/5"></div>
          
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 px-6 py-2 rounded-full border border-white/20" style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}>
            <span className="text-white font-semibold text-sm">Our Commitment</span>
          </div>
          
          <h3 className="relative text-2xl md:text-3xl font-bold text-white mb-4 mt-4">
            Building Intelligent{' '}
            <span className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] bg-clip-text text-transparent">
              Digital Futures
            </span>
          </h3>
          <p className="relative text-white/60 text-lg max-w-3xl mx-auto">
            We don't just deliver projects—we forge long-term partnerships. Your success is our mission, and we're invested in helping you achieve sustainable growth through innovative technology solutions.
          </p>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
